import {
	type ConnectorRowsFailureReason,
	readConnectorRowsFromLocalStorage,
} from './connectors/readConnectorRowsFromLocalStorage.ts';
import { resolvePageProducts } from './page-links/resolvePageProducts.ts';
import { selectQualifyingProducts } from './connectors/selectQualifyingProducts.ts';
import { type ThirdPartyPromptState } from './thirdPartyPromptTypes.ts';

export type ThirdPartyPromptInputs = {
	userId: string;
	cloudId: string;
	/** Undefined while the query loads. Repeated addresses need no de-duplication. */
	pageAddresses: readonly string[] | undefined;
};

export type ThirdPartyPromptDiagnostics = {
	connectorRowsFailureReason: ConnectorRowsFailureReason | null;
	connectorRowCount: number;
	candidateCount: number;
	addressCount: number;
	unmatchedAddressCount: number;
};

export type ThirdPartyPromptResult = {
	state: ThirdPartyPromptState;
	/** Size of the qualifying set. Zero whenever the state is `none`. */
	count: number;
	diagnostics: ThirdPartyPromptDiagnostics;
};

const NO_PROMPT: ThirdPartyPromptState = { kind: 'none' };

/**
 * Decides whether a connect prompt can run for this page. Synchronous and
 * effect free, so both sides of hydration derive the same answer from the same three inputs. Candidates
 * come first, so the page links are never read when nothing could match them.
 */
export const getThirdPartyPrompt = ({
	userId,
	cloudId,
	pageAddresses,
}: ThirdPartyPromptInputs): ThirdPartyPromptResult => {
	const { rows, failureReason } = readConnectorRowsFromLocalStorage({ userId, cloudId });

	const products = selectQualifyingProducts(rows);
	// Distinct, so a repeated link does not inflate the address counters.
	const addresses = products.length === 0 || !pageAddresses ? [] : [...new Set(pageAddresses)];
	const { appIds, unmatchedAddressCount } = resolvePageProducts(addresses);

	// `appId` is widened to `string` in the contract, so compare against a read-only view of the set.
	const linkedAppIds: ReadonlySet<string> = appIds;
	const linked = products.filter(({ appId }) => linkedAppIds.has(appId));

	const diagnostics: ThirdPartyPromptDiagnostics = {
		connectorRowsFailureReason: failureReason,
		connectorRowCount: rows.length,
		candidateCount: products.length,
		addressCount: addresses.length,
		unmatchedAddressCount,
	};

	// Tenant-wide connectors are not page candidates. Hold until outgoing links exist.
	if (products.length > 0 && pageAddresses === undefined) {
		return { state: { kind: 'pending' }, count: products.length, diagnostics };
	}

	const [product, ...rest] = linked;

	if (!product) {
		return { state: NO_PROMPT, count: 0, diagnostics };
	}

	if (rest.length === 0) {
		return { state: { kind: 'single', product }, count: 1, diagnostics };
	}

	return { state: { kind: 'many', candidates: linked }, count: linked.length, diagnostics };
};
